import { useEffect, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import sseService from '../services/sseService';

/**
 * Custom hook for managing SSE connection and real-time message updates
 * @param {Object} options - Hook options
 * @param {Function} options.onNewMessage - Callback for new messages
 * @param {Function} options.onStatusUpdate - Callback for status updates
 * @param {Function} options.onAssignmentUpdate - Callback for assignment updates
 * @param {Function} options.onConnectionChange - Callback for connection status changes
 * @returns {Object} SSE connection state and controls
 */
const useSSE = (options = {}) => {
  const accessToken = useSelector(state => state.auth.access_token?.token);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const optionsRef = useRef(options);

  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Connection event handlers
  const handleConnected = useCallback(() => {
    setConnectionStatus('connected');
    if (optionsRef.current.onConnectionChange) {
      optionsRef.current.onConnectionChange('connected');
    }
  }, []);

  const handleDisconnected = useCallback(() => {
    setConnectionStatus('disconnected');
    if (optionsRef.current.onConnectionChange) {
      optionsRef.current.onConnectionChange('disconnected');
    }
  }, []);

  const handleError = useCallback((data) => {
    console.error('SSE: Connection error', data);
    setConnectionStatus('error');
    if (optionsRef.current.onConnectionChange) {
      optionsRef.current.onConnectionChange('error', data);
    }
  }, []);

  const handleMaxReconnectAttempts = useCallback(() => {
    console.error('SSE: Max reconnection attempts reached');
    setConnectionStatus('failed');
    if (optionsRef.current.onConnectionChange) {
      optionsRef.current.onConnectionChange('failed');
    }
  }, []);

  const handleMessage = useCallback((data) => {
    setLastMessage(data);

    // Route to appropriate handler based on message type
    switch (data.type) {
      case 'message':
        if (optionsRef.current.onNewMessage) {
          optionsRef.current.onNewMessage(data);
        }
        break;
      case 'status_update':
        if (optionsRef.current.onStatusUpdate) {
          optionsRef.current.onStatusUpdate(data);
        }
        break;
      case 'conversation_assignment':
        if (optionsRef.current.onAssignmentUpdate) {
          optionsRef.current.onAssignmentUpdate(data);
        }
        break;
      default:
        console.warn('SSE: Unknown message type:', data.type);
    }
  }, []);

  // Set up event listeners
  useEffect(() => {
    sseService.addEventListener('connected', handleConnected);
    sseService.addEventListener('disconnected', handleDisconnected);
    sseService.addEventListener('error', handleError);
    sseService.addEventListener('maxReconnectAttemptsReached', handleMaxReconnectAttempts);
    sseService.addEventListener('message', handleMessage);

    return () => {
      sseService.removeEventListener('connected', handleConnected);
      sseService.removeEventListener('disconnected', handleDisconnected);
      sseService.removeEventListener('error', handleError);
      sseService.removeEventListener('maxReconnectAttemptsReached', handleMaxReconnectAttempts);
      sseService.removeEventListener('message', handleMessage);
    };
  }, [handleConnected, handleDisconnected, handleError, handleMaxReconnectAttempts, handleMessage]);

  // Connect/disconnect based on access token
  useEffect(() => {
    if (accessToken) {
      sseService.connect(accessToken, {
        onConnected: handleConnected,
        onError: handleError,
        onMessage: handleMessage,
        onNewMessage: optionsRef.current.onNewMessage,
        onStatusUpdate: optionsRef.current.onStatusUpdate,
        onAssignmentUpdate: optionsRef.current.onAssignmentUpdate,
        accessToken
      });
    } else {
      sseService.disconnect();
    }

    // Cleanup on unmount
    return () => {
      sseService.disconnect();
    };
  }, [accessToken, handleConnected, handleError, handleMessage]);

  // Manual connect function
  const connect = useCallback(() => {
    if (accessToken) {
      sseService.connect(accessToken, {
        onConnected: handleConnected,
        onError: handleError,
        onMessage: handleMessage,
        onNewMessage: optionsRef.current.onNewMessage,
        onStatusUpdate: optionsRef.current.onStatusUpdate,
        onAssignmentUpdate: optionsRef.current.onAssignmentUpdate,
        accessToken
      });
    }
  }, [accessToken, handleConnected, handleError, handleMessage]);

  // Manual disconnect function
  const disconnect = useCallback(() => {
    sseService.disconnect();
  }, []);

  // Get current connection status
  const getStatus = useCallback(() => {
    return sseService.getStatus();
  }, []);

  return {
    connectionStatus,
    lastMessage,
    connect,
    disconnect,
    getStatus,
    isConnected: connectionStatus === 'connected'
  };
};

export default useSSE;
